import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import regressionService from "../services/regressionService";
import WorkflowDiagram from './WorkflowDiagram';
import LoginModal from './LoginModal';
import { authService } from '../services/authService';
import { projectsService } from '../services/projectsService';
import ProjectModal from './ProjectModal';
import UserSection from './UserSection';
import Sidebar from './Sidebar';
import ChatInterface from './ChatInterface';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';



export const Subpage = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [waitingForOutcome, setWaitingForOutcome] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [pendingModelData, setPendingModelData] = useState(null);
  const [botMessageHeight, setBotMessageHeight] = useState(0);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false); // Add this line
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]); // Add this
  const [imagePreview, setImagePreview] = useState([]); // Add this
  const [username, setUsername] = useState(''); // Add username state for Firestore

  // Refs
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const botMessageRef = useRef(null);
  const fileUploadRef = useRef(null); // New ref for combined file upload

  // Helper functions
  const isLinearRegressionRequest = (input) => {
    const keywords = ['linear regression', 'regression analysis', 'run regression', 'linear model'];
    return keywords.some(keyword => input.toLowerCase().includes(keyword));
  };

  const extractOutcomeVariable = (input, columns) => {
    const lowerInput = input.toLowerCase();
    const patterns = [
      /(?:outcome|y|dependent)\s+(?:variable\s+)?(?:is\s+)?([a-zA-Z_][a-zA-Z0-9_]*)/i,
      /predict\s+([a-zA-Z_][a-zA-Z0-9_]*)/i,
      /([a-zA-Z_][a-zA-Z0-9_]*)\s+(?:is\s+)?(?:the\s+)?(?:outcome|y|dependent)/i
    ];
    
    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match && columns.includes(match[1])) {
        return match[1];
      }
    }
    
    return columns.find(col => 
      lowerInput.includes(col.toLowerCase()) || 
      col.toLowerCase().includes(lowerInput.trim())
    ) || null;
  };

  const downloadPlot = (downloadId, plotType) => {
    if (!downloadId) return;
    
    const downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/download-plot/${plotType}/${downloadId}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${plotType}_plot.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Load username from Firestore
  const loadUsernameFromFirestore = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUsername(userData.username || '');
      }
    } catch (error) {
      console.error('Error loading username from Firestore:', error);
    }
  };

  // Handle username update from SettingsPage
  const handleUsernameUpdate = (newUsername) => {
    setUsername(newUsername);
  };

  // CSV handling
  const handleCSVUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/upload-csv`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        setUploadedFile({
          filename: result.filename,
          columns: result.columns
        });
        setWaitingForOutcome(true);
        setWorkflowStep(1); // Step 1: Upload Data completed
        
        setTimeout(() => setHasStartedChat(true), 300);
        
        setTimeout(() => {
          setMessages(prev => [...prev, {
            sender: "bot",
            text: result.message,
            hasPreview: result.hasPreview,
            previewData: result.previewData,
            isNewMessage: true
          }]);
        }, 700);
      } else {
        alert('Upload failed: ' + result.error);
      }
    } catch (error) {
      alert('Upload error: ' + error.message);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      handleCSVUpload(file);
    } else if (file) {
      alert('Please select a CSV file');
    }
  };

  const runCSVRegression = async (outcomeVariable) => {
    if (!uploadedFile) {
      alert('No file uploaded');
      return;
    }

    setIsSending(true);
    setMessages(prev => [...prev, { sender: "bot", text: "•" }]);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/regression-from-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: uploadedFile.filename,
          y_column: outcomeVariable
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setPendingModelData(result.raw_data);
        setWorkflowStep(2); // Step 2: Run Regression completed (changed from 3)
        
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            sender: "bot",
            text: result.results,
            showActionButtons: result.show_action_buttons,
            regressionType: result.regression_type,
            isNewMessage: true
          };
          return updated;
        });
        
        setWaitingForOutcome(false);
      } else {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            sender: "bot",
            text: `Error: ${result.error}`,
            isNewMessage: true
          };
          return updated;
        });
      }
    } catch (error) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          sender: "bot",
          text: `Error running regression: ${error.message}`,
          isNewMessage: true
        };
        return updated;
      });
    } finally {
      setIsSending(false);
    }
  };

  // Action handlers
  const handleModelDiagnostics = async () => {
    if (!pendingModelData) return;
    
    setIsSending(true);
    setInput("");
    
    setMessages(prev => [...prev, { 
      sender: "user", 
      text: "Show model diagnostics", 
      isNewMessage: true 
    }]);
    
    await new Promise(resolve => setTimeout(resolve, 200));
    setMessages(prev => [...prev, { sender: "bot", text: "•" }]);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/model-diagnostics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_data: pendingModelData })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setWorkflowStep(3); // Step 3: Diagnostics completed (changed from 4)
        
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            sender: "bot",
            text: result.message,
            plotUrl: result.plot_url,
            regLineUrl: result.regression_line_url,
            hasPlot: !!result.plot_url,
            hasRegLine: !!result.regression_line_url,
            residualsDownloadId: result.residuals_download_id,
            regressionLineDownloadId: result.regression_line_download_id,
            isNewMessage: true
          };
          return updated;
        });
      }
    } catch (error) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          sender: "bot",
          text: `Error generating diagnostics: ${error.message}`,
          isNewMessage: true
        };
        return updated;
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleInterpretResults = async () => {
    if (!pendingModelData) return;
    
    setIsSending(true);
    setInput("");
    
    setMessages(prev => [...prev, { 
      sender: "user", 
      text: "Interpret the results", 
      isNewMessage: true 
    }]);
    
    await new Promise(resolve => setTimeout(resolve, 200));
    setMessages(prev => [...prev, { sender: "bot", text: "•" }]);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/interpret-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_data: pendingModelData })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setWorkflowStep(4); // Step 4: Interpretation completed (changed from 5)
        
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            sender: "bot",
            text: result.interpretation,
            isNewMessage: true
          };
          return updated;
        });
      }
    } catch (error) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          sender: "bot",
          text: `Error generating interpretation: ${error.message}`,
          isNewMessage: true
        };
        return updated;
      });
    } finally {
      setIsSending(false);
    }
  };

  // Project management functions
  const loadUserProjects = async (userId) => {
    if (!userId) {
      console.log('❌ No user ID provided to loadUserProjects');
      return;
    }
    
    console.log('🔄 Loading projects for user:', userId);
    
    try {
      const result = await projectsService.getUserProjects(userId);
      console.log('📋 Load projects result:', result);
      
      if (result.success) {
        console.log('✅ Setting projects:', result.projects);
        console.log('📊 Number of projects found:', result.projects.length);
        setProjects(result.projects);
      } else {
        console.error('❌ Failed to load projects:', result.error);
      }
    } catch (error) {
      console.error('❌ Error in loadUserProjects:', error);
    }
  };

  const handleCreateProject = async (projectName) => {
    if (!user?.uid) {
      alert('You must be logged in to create a project');
      return;
    }
    
    console.log('Creating project:', projectName, 'for user:', user.uid); // Debug log
    
    setIsProjectLoading(true);
    
    try {
      const result = await projectsService.createProject(user.uid, projectName);
      
      console.log('Project creation result:', result); // Debug log
      
      if (result.success) {
        setProjects(prev => [result.project, ...prev]);
        setShowProjectModal(false);
        // Auto-select the new project
        setCurrentProject(result.project);
        // Clear current messages to start fresh
        setMessages([]);
        setHasStartedChat(false);
        setWorkflowStep(0);
        
        console.log('Project created successfully:', result.project); // Debug log
      } else {
        console.error('Project creation failed:', result.error);
        alert('Failed to create project: ' + result.error);
      }
    } catch (error) {
      console.error('Error in handleCreateProject:', error);
      alert('An error occurred while creating the project: ' + error.message);
    } finally {
      setIsProjectLoading(false);
    }
  };

  const handleSelectProject = async (project) => {
    setCurrentProject(project);
    // Load project messages
    setMessages(project.messages || []);
    setHasStartedChat(project.messages && project.messages.length > 0);
    
    // Update project as active
    await projectsService.updateProject(project.id, { isActive: true });
    
    // Update other projects to inactive
    projects.forEach(async (p) => {
      if (p.id !== project.id && p.isActive) {
        await projectsService.updateProject(p.id, { isActive: false });
      }
    });
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"?`)) {
      return;
    }
    
    const result = await projectsService.deleteProject(projectId);
    
    if (result.success) {
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // If deleted project was current, clear it
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setMessages([]);
        setHasStartedChat(false);
        setWorkflowStep(0);
      }
    } else {
      alert('Failed to delete project: ' + result.error);
    }
  };

  // Auto-save messages when they change
  const saveCurrentProject = useCallback(async () => {
    if (currentProject && messages.length > 0) {
      await projectsService.saveProjectMessages(currentProject.id, messages);
    }
  }, [currentProject, messages]);

  // Create message with images for API
  const createMessageWithImages = async (text, imageFiles) => {
    const content = [];
    
    // Add text content
    if (text && text.trim()) {
      content.push({
        type: "text",
        text: text
      });
    }
    
    // Add images if present
    if (imageFiles && imageFiles.length > 0) {
      for (const imageFile of imageFiles) {
        try {
          const base64Image = await convertImageToBase64(imageFile);
          content.push({
            type: "image_url",
            image_url: {
              url: base64Image,
              detail: "high" // or "low" for faster processing
            }
          });
        } catch (error) {
          console.error('Error converting image to base64:', error);
        }
      }
    }
    
    // If no content, add a default message
    if (content.length === 0) {
      content.push({
        type: "text",
        text: "Hello"
      });
    }
    
    return {
      role: "user",
      content: content
    };
  };

  // Helper function to convert image file to base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Main send handler
  const handleSend = async () => {
    if ((input.trim() === "" && selectedImages.length === 0) || isSending) return;
    
    // Only check message limit if user is NOT authenticated
    if (!isAuthenticated && userMessageCount >= 10) {
      setShowLoginModal(true);
      return;
    }
    
    // Only increment message count if user is not authenticated
    if (!isAuthenticated) {
      setUserMessageCount(prev => prev + 1);
    }
    
    if (!hasStartedChat) {
      setHasStartedChat(true);
    }
    
    setIsSending(true);
    
    // Convert images to base64 for storage AND display
    const imageData = [];
    if (selectedImages.length > 0) {
      for (const img of imagePreview) {
        try {
          const base64 = await convertImageToBase64(img.file);
          imageData.push({
            url: base64, // Use base64 for display instead of blob URL
            base64: base64, // Also store base64 for API calls
            name: img.name
          });
        } catch (error) {
          console.error('Error converting image:', error);
        }
      }
    }
    
    // Create user message with text and images
    const userMessage = {
      sender: "user",
      text: input,
      images: imageData, // Use the converted image data
      hasImages: selectedImages.length > 0
    };
    
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    
    const currentInput = input;
    const currentImages = selectedImages;
    
    // Clear input and images
    setInput("");
    clearAllImages();
    
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Handle CSV outcome variable selection (existing logic)
    if (waitingForOutcome && uploadedFile) {
      const outcomeVariable = extractOutcomeVariable(currentInput, uploadedFile.columns);
      if (outcomeVariable) {
        setMessages(prev => [...prev, {
          sender: "bot",
          text: `Great! I'll use **${outcomeVariable}** as the outcome variable and automatically include all other numeric columns as predictors.\n\nRunning regression analysis...`,
          isNewMessage: true
        }]);
        await runCSVRegression(outcomeVariable);
        setIsSending(false);
        return;
      } else {
        setMessages(prev => [...prev, {
          sender: "bot",
          text: `I couldn't identify "${currentInput}" as a column in your dataset.\n\n**Available columns:** ${uploadedFile.columns.join(', ')}\n\nPlease specify one of these column names.`,
          isNewMessage: true
        }]);
        setIsSending(false);
        return;
      }
    }
    
    setMessages((prevMessages) => [...prevMessages, { sender: "bot", text: "•" }]);
    
    try {
      if (isLinearRegressionRequest(currentInput)) {
        // Existing regression logic
        const response = await regressionService.runLinearRegression();
        setPendingModelData(response.raw_data);
        
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = { 
            sender: "bot", 
            text: response.results,
            isNewMessage: true,
            showActionButtons: response.show_action_buttons,
            regressionType: response.regression_type
          };
          return updatedMessages;
        });
      } else {
        // Prepare messages for ChatGPT API
        const chatMessages = messages.slice(-10).map((msg) => {
          if (msg.hasImages && msg.images?.length > 0 && msg.sender === "user") {
            const content = [];
            
            if (msg.text && msg.text.trim()) {
              content.push({
                type: "text",
                text: msg.text
              });
            }
            
            // Add stored images using base64 data
            msg.images.forEach(img => {
              if (img.base64) {
                content.push({
                  type: "image_url",
                  image_url: {
                    url: img.base64,
                    detail: "high"
                  }
                });
              }
            });
            
            return {
              role: "user",
              content: content
            };
          } else {
            return {
              role: msg.sender === "user" ? "user" : "assistant",
              content: [{
                type: "text",
                text: msg.text
              }]
            };
          }
        });

        // Add current message
        const currentMessage = await createMessageWithImages(currentInput, currentImages);

        // ChatGPT API call
        const response = await axios.post(
          "https://api.openai.com/v1/chat/completions",
          {
            model: "gpt-4o-mini", // Fixed model name
            messages: [
              {
                role: "system",
                content: `You MUST use LaTeX formatting for ALL mathematical expressions and symbols.

                          CRITICAL RULES:
                          - Wrap ALL math in dollar signs: $...$
                          - Even single variables like σ, ω, α must be wrapped: $\\sigma$, $\\omega$, $\\alpha$
                          - Never output bare LaTeX symbols like \\sigma - always wrap them: $\\sigma$
                          - For inline math: $P(A|B) = \\frac{P(B|A) \\cdot P(A)}{P(B)}$
                          - For display math: $$P(A|B) = \\frac{P(B|A) \\cdot P(A)}{P(B)}$$
                          - Use double backslashes: \\sigma, \\omega, \\alpha, \\frac, etc.

                          You can analyze images that users upload. Provide detailed explanations about mathematical content, diagrams, data visualizations, equations, graphs, charts, or any other content present in the images.

                          EXAMPLES:
                          CORRECT: The complex variable is $s = \\sigma + i\\omega$ where $\\sigma$ is the real part.
                          WRONG: The complex variable is s = \\sigma + i \\omega where \\sigma is the real part.

                          Always wrap mathematical content in dollar signs. No exceptions.`
              },
              ...chatMessages.slice(-10), // Keep last 10 messages for context
              currentMessage
            ],
            max_tokens: 4096,
            temperature: 0.7,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            },
          }
        );
        
        const botMessage = response.data.choices[0].message.content;
        
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = { 
            sender: "bot", 
            text: botMessage,
            isNewMessage: true
          };
          return updatedMessages;
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        updatedMessages[updatedMessages.length - 1] = { 
          sender: "bot", 
          text: `Sorry, I encountered an error: ${error.message}`,
          isNewMessage: true
        };
        return updatedMessages;
      });
    } finally {
      setIsSending(false);
    }
  };

  // Login handlers
  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setShowLoginModal(false);
    // Reset message count after successful login
    setUserMessageCount(0);
  };

  const handleCloseLogin = () => {
    setShowLoginModal(false);
  };

  // Add logout handler
  const handleLogout = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setUserMessageCount(0);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Effects
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === 'bot' && botMessageRef.current) {
      const height = botMessageRef.current.offsetHeight;
      setBotMessageHeight(height);
    }
  }, [messages]);

  useEffect(() => {
    if (!messagesEndRef.current) return;
    
    const chatContainer = messagesEndRef.current.parentElement;
    if (!chatContainer) return;
    
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.sender !== 'user') return;

    setTimeout(() => {
      try {
        const userMessages = chatContainer.querySelectorAll('.user-message');
        const lastUserMessage = userMessages[userMessages.length - 1];
        
        if (lastUserMessage) {
          lastUserMessage.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start'
          });
        }
      } catch (error) {
        console.error('Scroll error:', error);
      }
    }, 200);
  }, [messages]);

  useEffect(() => {
    console.log('🔐 Setting up auth state listener...');
    
    const unsubscribe = authService.onAuthStateChanged((user) => {
      console.log('🔐 Auth state changed:', user);
      
      if (user && user.emailVerified) {
        const userData = {
          uid: user.uid,
          name: user.displayName || user.email.split('@')[0],
          email: user.email,
          photoURL: user.photoURL
        };
        console.log('✅ User authenticated:', userData);
        setUser(userData);
        setIsAuthenticated(true);
        setUserMessageCount(0);
        
        // Load username from Firestore
        loadUsernameFromFirestore(userData.uid);
        
        // Load user's projects
        console.log('🔄 About to load projects for user:', userData.uid);
        loadUserProjects(userData.uid);
      } else {
        console.log('❌ User not authenticated or email not verified');
        setUser(null);
        setIsAuthenticated(false);
        setProjects([]);
        setCurrentProject(null);
        setUsername(''); // Clear username when not authenticated
      }
    });

    return () => {
      console.log('🔐 Cleaning up auth listener');
      unsubscribe();
    };
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Auto-save messages when they change
  useEffect(() => {
    if (currentProject && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        saveCurrentProject();
      }, 2000); // Save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId);
    }
  }, [messages, currentProject, saveCurrentProject]);

  // Image handling functions
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length !== files.length) {
      alert('Please select only image files (PNG, JPG, GIF, etc.)');
    }
    
    if (validImages.length > 0) {
      // Limit to 5 images max
      const limitedImages = validImages.slice(0, 5);
      setSelectedImages(prev => [...prev, ...limitedImages].slice(0, 5));
      
      // Create preview URLs
      const newPreviews = limitedImages.map(file => ({
        file,
        url: URL.createObjectURL(file),
        name: file.name
      }));
      
      setImagePreview(prev => [...prev, ...newPreviews].slice(0, 5));
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => {
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearAllImages = () => {
    // Revoke all URLs to free memory
    imagePreview.forEach(preview => URL.revokeObjectURL(preview.url));
    setSelectedImages([]);
    setImagePreview([]);
  };

  // New handler for file uploads (CSV and images)
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        // Handle image files
        handleImageSelect(event);
      } else if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        // Handle CSV files
        handleFileSelect(event);
      } else {
        // Show error for unsupported file types
        console.warn('Unsupported file type:', file.type);
        alert('Only CSV and image files are supported.');
      }
    });
    
    // Clear the input
    event.target.value = '';
  };

  return (
    <div className="chatgpt-container">
      <UserSection 
        isAuthenticated={isAuthenticated}
        
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        setShowLoginModal={setShowLoginModal}
        handleLogout={handleLogout}
      />

      <Sidebar 
        isSidebarVisible={isSidebarVisible}
        setIsSidebarVisible={setIsSidebarVisible}
        isAuthenticated={isAuthenticated}
        user={user}
        username={username}
        projects={projects}
        currentProject={currentProject}
        setShowProjectModal={setShowProjectModal}
        handleSelectProject={handleSelectProject}
        handleDeleteProject={handleDeleteProject}
        setShowLoginModal={setShowLoginModal}
        onLogout={handleLogout}
      />

      <ChatInterface 
        isSidebarVisible={isSidebarVisible}
          hasStartedChat={hasStartedChat}
          messages={messages}
          botMessageHeight={botMessageHeight}
          messagesEndRef={messagesEndRef}
          botMessageRef={botMessageRef}
          handleModelDiagnostics={handleModelDiagnostics}
          handleInterpretResults={handleInterpretResults}
          isSending={isSending}
          downloadPlot={downloadPlot}
          input={input}
          setInput={setInput}
          handleSend={handleSend}
          waitingForOutcome={waitingForOutcome}
          selectedImages={selectedImages}
          imagePreview={imagePreview}
          removeImage={removeImage}
          fileInputRef={fileInputRef}
          fileUploadRef={fileUploadRef}
          handleFileSelect={handleFileSelect}
          handleImageSelect={handleImageSelect}
          handleFileUpload={handleFileUpload}
        />
      
      <WorkflowDiagram currentStep={workflowStep} />
      <LoginModal 
        isOpen={showLoginModal}
        onClose={handleCloseLogin}
        onLogin={handleLogin}
      />
      <ProjectModal 
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onCreateProject={handleCreateProject}
        isLoading={isProjectLoading}
      />
    </div>
  );
};